import {
  coordKey,
  facingTowardAttackTarget,
  type Coord,
  type FacingDir,
  type GameState,
  type Unit,
} from "@dailywars/core/engine";

import type { CanvasBoardAnimation } from "#/game/animation/types";

import { getAnimatedFacing, getAnimatedPosition, isAnimationComplete } from "./boardAnimations";
import { TILE_SIZE, type CanvasViewState } from "./boardConstants";
export interface UnitDrawLayout {
  pos: Coord;
  drawX: number;
  drawY: number;
  facing: FacingDir;
}

function resolveUnitDrawPosition({
  unit,
  viewState,
  animation,
  nowMs,
}: {
  unit: Unit;
  viewState: CanvasViewState;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): Coord {
  const previewForThisUnit =
    viewState.previewUnit?.unitId === unit.id ? viewState.previewUnit : null;
  const previewPos = previewForThisUnit?.pos ?? null;
  const isAnimatingThisUnit = animation?.type === "move" && animation.unitId === unit.id;
  const isPreviewAnimatingThisUnit = previewPos && viewState.previewAnimation?.unitId === unit.id;
  const previewAnimatedPos = isPreviewAnimatingThisUnit
    ? getAnimatedPosition(viewState.previewAnimation!, nowMs)
    : null;
  if (isAnimatingThisUnit) {
    return getAnimatedPosition(animation, nowMs);
  }
  return previewAnimatedPos ?? previewPos ?? unit.pos;
}

function resolveUnitDrawFacing({
  unit,
  viewState,
  animation,
  nowMs,
  previewPos,
  previewTargetPos,
}: {
  unit: Unit;
  viewState: CanvasViewState;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
  previewPos: Coord | null;
  previewTargetPos: Coord | null | undefined;
}): FacingDir {
  const isAnimatingThisUnit = animation?.type === "move" && animation.unitId === unit.id;
  if (isAnimatingThisUnit) {
    return getAnimatedFacing({ animation, nowMs, fallback: unit.facing });
  }
  if (previewTargetPos && previewPos) {
    const previewMoveAnimation =
      viewState.previewAnimation?.unitId === unit.id ? viewState.previewAnimation : null;
    return facingTowardAttackTarget({
      from: previewPos,
      to: previewTargetPos,
      path: previewMoveAnimation?.path,
      startPos: previewMoveAnimation?.from ?? unit.pos,
      currentFacing: unit.facing,
    });
  }
  const previewMoveAnimation =
    previewPos && viewState.previewAnimation?.unitId === unit.id
      ? viewState.previewAnimation
      : null;
  if (previewMoveAnimation) {
    const previewNowMs = isAnimationComplete(previewMoveAnimation, nowMs)
      ? previewMoveAnimation.startedAtMs + previewMoveAnimation.durationMs
      : nowMs;
    return getAnimatedFacing({
      animation: previewMoveAnimation,
      nowMs: previewNowMs,
      fallback: unit.facing,
    });
  }
  return unit.facing;
}

export function collectOccupiedUnitTileKeys({
  state,
  viewState,
  animation,
  nowMs,
}: {
  state: Pick<GameState, "units">;
  viewState: CanvasViewState;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): Set<string> {
  const keys = new Set<string>();
  for (const unit of state.units) {
    const { pos } = resolveUnitDrawLayout({ unit, viewState, animation, nowMs });
    keys.add(coordKey(pos));
  }
  return keys;
}

export function resolveUnitDrawLayout({
  unit,
  viewState,
  animation,
  nowMs,
}: {
  unit: Unit;
  viewState: CanvasViewState;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): UnitDrawLayout {
  const previewForThisUnit =
    viewState.previewUnit?.unitId === unit.id ? viewState.previewUnit : null;
  const previewPos = previewForThisUnit?.pos ?? null;
  const pos = resolveUnitDrawPosition({ unit, viewState, animation, nowMs });
  const facing = resolveUnitDrawFacing({
    unit,
    viewState,
    animation,
    nowMs,
    previewPos,
    previewTargetPos: previewForThisUnit?.targetPos,
  });
  return { pos, drawX: pos.x * TILE_SIZE, drawY: pos.y * TILE_SIZE, facing };
}
